import { FC } from 'react';
import styled from 'styled-components';

import { getTagColor } from '@/entities/vfo/helpers';
import { CASE_BACKGROUND_STYLES } from '@/features/vfo/constants';
import { ApiCaseResponseV2 } from '@/shared/api';
import { COLORS } from '@/shared/constants/colors';
import { useAppRouter } from '@/shared/hooks/app/useAppRouter';
import { useAuthStore } from '@/shared/store/auth.store';
import { Icon } from '@/shared/uikit/Icon';

import { useMembers } from '../../hooks/useMembers';

type CaseItemProps = {
  data: ApiCaseResponseV2;
};

export const CaseItem: FC<CaseItemProps> = ({ data }) => {
  const bgConfig = CASE_BACKGROUND_STYLES[data.background];
  const router = useAppRouter();
  const { account } = useAuthStore();
  const { members } = useMembers();

  const memberId = data.isPrivateCase
    ? data.members.find((m) => m.profileId !== account?.id)?.profileId
    : undefined;

  const member = memberId ? members.find((m) => m.profileId === memberId) : undefined;

  return (
    <Container
      onClick={() => {
        router.pushShallow({ pathname: `/vfo/case/${data.id}` });
      }}
    >
      <Preview
        style={{
          background: member?.tag ? getTagColor(member.tag) : bgConfig.background,
          color: bgConfig.iconColor,
        }}
      >
        {member ? (
          <Initials>
            {member.firstName?.[0]} {member.lastName?.[0]}
          </Initials>
        ) : (
          <Icon
            name={data.isGeneralCase ? 'folder-star' : bgConfig.icon}
            size="s64"
            style={{ opacity: 0.7 }}
          />
        )}
      </Preview>
      <Content>
        <b className="truncate">{data.title}</b>
        <Statistics>
          <DocumentInfoItem>
            <Icon name="user" size="s16" />
            <span>{data.members.length - 1}</span> {/* -1 because of the founder  */}
          </DocumentInfoItem>
          <DocumentInfoItem>
            <Icon name="file-text" size="s16" />
            <span>{data.caseDocuments.length}</span>
          </DocumentInfoItem>
          <DocumentInfoItem>
            <Icon name="list-checks" size="s16" />
            <span>{data.tasks.length}</span>
          </DocumentInfoItem>
        </Statistics>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid ${COLORS.systemDark100};
  min-width: 133px;
  width: 100%;
  display: block;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.systemDark200aCC};
  }
`;

const Content = styled.div`
  padding: 12px 16px;
  background: ${COLORS.white};
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
`;

const Preview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 0;
  color: ${COLORS.systemSky200};
  background: ${COLORS.systemSky100};
  background-repeat: no-repeat !important;
  background-size: cover !important;
  height: 100px;
`;

const Statistics = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
`;

const Initials = styled.div`
  font-size: 48px;
  font-weight: 700;
  line-height: 52px;
  color: ${COLORS.white};
  opacity: 0.9;
`;

const DocumentInfoItem = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
